// Plan 08-08 — particle emitter with reduced-motion suppression.
// Plan 30-01 — rune-carve + mythic-sparkle added.
// Plan 32-01 — stone-fall + stone-carve added (LetterCubeSprite Anchor
//              lifecycle emits — UI-SPEC §2 row 10).
//
// Per UI-SPEC §"Reduced-motion substitution table":
//   12 named particle effects suppressed entirely when reduced motion is on:
//     dust-puff, coin-spark, magic-bloom, player-a-shimmer, player-b-ember,
//     dark-pulse, ribbon-burst, mote (atmospheric drift),
//     rune-carve, mythic-sparkle (Runespring),
//     stone-fall, stone-carve (Phase 32 Anchor lifecycle).
//
// Implementation contract:
//   - Every emit call passes the current `reduced` mode in (caller reads
//     $reducedMotion.get() at the call-site so the emitter stays a pure
//     function for testability).
//   - When reduced=true → emit() returns []. No allocations, no Pixi
//     particle resources spun up.
//   - When reduced=false → emit() returns `count` Particle objects. Layer
//     code (Plan 04 EffectLayer) hands these to PixiJS sprite pools.
//
// Reduced-motion gate (Plan 21-07 / INFRA-06 — was Plan 04 EffectLayer's
// design requirement): every emitter reads the motion mode passed in via
// opts.reduced at the call-site and short-circuits with an empty result
// when reduced=true (see emit() body below). Resolves the original Plan
// 04 design-doc TO_DO; the contract has been live since this file landed.

import type { ParticleSystem } from './particle-system'

export type ParticleKind =
  | 'dust-puff'
  | 'coin-spark'
  | 'magic-bloom'
  | 'player-a-shimmer'
  | 'player-b-ember'
  | 'dark-pulse'
  | 'ribbon-burst'
  | 'mote'
  // Plan 30-01 — Runespring kinds (UI-SPEC §2 z=8 particleAnchor).
  // 'rune-carve' is the spawn-in burst for refilled cells (caller emits
  // count=6 with 200ms lifetime). 'mythic-sparkle' is the idle Mythic
  // emitter — caller throttles to ~0.5/s/cell; emit() spawns one particle
  // per call. Both honor reduced:true → [] (suspends under reduced motion).
  | 'rune-carve'
  | 'mythic-sparkle'
  // Plan 32-01 — LetterCubeSprite state-transition emits (UI-SPEC §2
  // row 10 + §4 Reveal FSM + CONTEXT.md "New particle effects"):
  //   'stone-fall'  — Anchor → Hollow transition. Caller emits count=8
  //     (warm-grey crumble + Inkwell shards mix) with 350ms lifetime.
  //     Triggered on consume + by Quill Mark mode (destroyed Anchor).
  //   'stone-carve' — new-Anchor spawn (Phase B placement + Quill Claim).
  //     Caller emits count=10 (brass sparks + rune-glyph ghost) with
  //     500ms lifetime.
  // Both honor the reduced:true → [] contract (UI-SPEC §8 substitution
  // table — "stone-fall" + "stone-carve" both "suspended under reduced
  // motion; particles.ts emit() returns []").
  | 'stone-fall'
  | 'stone-carve'

/**
 * Canonical UI-SPEC §"Reduced-motion substitution table" particle kinds.
 * Length and set membership are pinned by particles.test.ts so any future
 * addition surfaces as an intentional UI-SPEC change.
 */
export const PARTICLE_KINDS: readonly ParticleKind[] = [
  'dust-puff',
  'coin-spark',
  'magic-bloom',
  'player-a-shimmer',
  'player-b-ember',
  'dark-pulse',
  'ribbon-burst',
  'mote',
  'rune-carve',
  'mythic-sparkle',
  // Plan 32-01 — anchor-lifecycle particle kinds (UI-SPEC §2 + CONTEXT.md).
  'stone-fall',
  'stone-carve',
] as const

export interface Particle {
  readonly id: number
  readonly kind: ParticleKind
}

export interface EmitOptions {
  readonly kind: ParticleKind
  readonly count: number
  readonly reduced: boolean
  /**
   * Polish Phase 2 — optional emit position (logical px, in the shared
   * EffectLayer/World coordinate space). When provided AND the emitter is
   * connected to a ParticleSystem AND reduced=false, the burst renders as
   * real GPU particles at (x, y). When absent (or no system connected), emit()
   * still returns the descriptor array — preserving the original data-only
   * contract for tests and non-rendered callers.
   */
  readonly x?: number
  readonly y?: number
}

/**
 * Particle emitter. Consumed by Plan 04 EffectLayer + future tween-driven
 * cinematic events (T1 / T6 / T12 reveals — particles dropped under reduced
 * motion per UI-SPEC).
 *
 * Stateless aside from a deterministic id counter (helps tests assert that
 * emitted particles are distinct). Resource pooling is the caller's
 * responsibility (Plan 04 EffectLayer + PixiJS sprite atlas).
 */
export class ParticleEmitter {
  private _seq = 0
  private _system: ParticleSystem | null = null

  /**
   * Connect this emitter to the shared GPU ParticleSystem (Polish Phase 2).
   * LetterLayer wires the match's system in so cube-lifecycle emits render.
   * Pass null to detach. No-op-safe before/without a system.
   */
  connect(system: ParticleSystem | null): void {
    this._system = system
  }

  emit(opts: EmitOptions): Particle[] {
    if (opts.reduced) return []
    // Drive the real render layer when connected and a position is known.
    // The system enforces the reduced-motion gate too (defense in depth).
    if (
      this._system &&
      opts.count > 0 &&
      opts.x !== undefined &&
      opts.y !== undefined
    ) {
      this._system.emit(opts.kind, opts.x, opts.y, {
        count: opts.count,
        reduced: false,
      })
    }
    if (opts.count <= 0) return []
    const out: Particle[] = []
    for (let i = 0; i < opts.count; i++) {
      this._seq += 1
      out.push({ id: this._seq, kind: opts.kind })
    }
    return out
  }
}
